import type { EscenarioPrueba, EstadoResultado } from '../types/pruebas';

/** Resultado tal como se muestra en la exportación (puede no existir aún). */
export interface ResultadoExport {
  estado: EstadoResultado | null;
  causa: string;
  actualizadoEn: Date | null;
  actualizadoPorNombre?: string;
}

function fechaLegible(d: Date | null): string {
  if (!d) return '—';
  return d.toLocaleString('es-PA', { dateStyle: 'medium', timeStyle: 'short' });
}

/** Genera el contenido Markdown de un escenario + su resultado actual. */
export function generarMarkdown(esc: EscenarioPrueba, resultado: ResultadoExport): string {
  const lineas: string[] = [];
  lineas.push(`# ${esc.id} · ${esc.titulo}`);
  lineas.push('');
  lineas.push(`**Área:** ${esc.area}`);
  lineas.push('');
  lineas.push('## Objetivo');
  lineas.push(esc.objetivo);
  lineas.push('');
  lineas.push('## Configuración inicial');
  esc.configuracionInicial.forEach((c) => lineas.push(`- [ ] ${c}`));
  lineas.push('');
  if (esc.cambiosConfiguracion?.length) {
    lineas.push('## Cambios de configuración durante la prueba');
    esc.cambiosConfiguracion.forEach((c) => lineas.push(`- [ ] ${c}`));
    lineas.push('');
  }
  lineas.push('## Paso a paso');
  esc.pasos.forEach((p, i) => lineas.push(`${i + 1}. **${p.actor}** — ${p.accion}`));
  lineas.push('');
  lineas.push('## Checkpoints (qué debe cumplirse)');
  esc.checkpoints.forEach((c) => lineas.push(`- [ ] ${c}`));
  lineas.push('');
  if (esc.notas) {
    lineas.push('## Notas');
    lineas.push(esc.notas);
    lineas.push('');
  }
  lineas.push('## Resultado');
  lineas.push(`- **Estado:** ${resultado.estado === 'pass' ? '✅ PASS' : resultado.estado === 'fail' ? '❌ FAIL' : 'Sin correr'}`);
  lineas.push(`- **Causa / observaciones:** ${resultado.causa || '—'}`);
  lineas.push(`- **Actualizado:** ${fechaLegible(resultado.actualizadoEn)}${resultado.actualizadoPorNombre ? ` por ${resultado.actualizadoPorNombre}` : ''}`);
  lineas.push('');
  return lineas.join('\n');
}

/** Descarga un string como archivo .md */
export function descargarMarkdown(esc: EscenarioPrueba, resultado: ResultadoExport): void {
  const contenido = generarMarkdown(esc, resultado);
  const blob = new Blob([contenido], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${esc.id}_${slug(esc.titulo)}.md`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function slug(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Abre una ventana con una vista IMPRIMIBLE del escenario y lanza el diálogo
 * de impresión del navegador — el usuario elige "Guardar como PDF". No usa
 * ninguna librería de PDF (no hay ninguna instalada en el proyecto); es el
 * enfoque estándar sin dependencias para "descargar PDF" desde el cliente.
 */
export function imprimirComoPdf(esc: EscenarioPrueba, resultado: ResultadoExport): void {
  const win = window.open('', '_blank', 'width=900,height=1000');
  if (!win) {
    alert('El navegador bloqueó la ventana de impresión. Habilite las ventanas emergentes para este sitio e intente de nuevo.');
    return;
  }

  const html = `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(esc.id)} — ${escapeHtml(esc.titulo)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, Segoe UI, Roboto, Arial, sans-serif; color: #1a1a1a; padding: 32px 40px; max-width: 800px; margin: 0 auto; }
  h1 { font-size: 20px; margin: 0 0 4px; }
  .area { color: #666; font-size: 13px; margin-bottom: 20px; }
  h2 { font-size: 14px; text-transform: uppercase; letter-spacing: 0.04em; color: #0B6E37; border-bottom: 1px solid #ddd; padding-bottom: 4px; margin: 22px 0 10px; }
  p { line-height: 1.5; font-size: 13.5px; }
  ul, ol { padding-left: 20px; margin: 6px 0; }
  li { line-height: 1.6; font-size: 13.5px; margin-bottom: 4px; }
  .paso-actor { font-weight: 600; color: #0B6E37; }
  .checkbox { display: inline-block; width: 12px; height: 12px; border: 1.5px solid #999; margin-right: 6px; vertical-align: middle; border-radius: 2px; }
  .resultado { margin-top: 20px; padding: 14px 16px; border: 1.5px solid #ccc; border-radius: 8px; }
  .resultado-campo { margin-bottom: 8px; font-size: 13.5px; }
  .resultado-campo b { display: inline-block; min-width: 120px; }
  .badge { display: inline-block; padding: 2px 10px; border-radius: 999px; font-size: 12px; font-weight: 700; }
  .badge-pass { background: #E8F5E9; color: #1B5E20; }
  .badge-fail { background: #FDECEA; color: #B71C1C; }
  .badge-none { background: #eee; color: #555; }
  .notas { background: #FFFBEB; border: 1px solid #FCD34D; border-radius: 8px; padding: 10px 14px; font-size: 13px; color: #7A5A12; }
  .causa-box { min-height: 60px; border: 1px solid #ccc; border-radius: 6px; padding: 8px; font-size: 13px; white-space: pre-wrap; }
  @media print { body { padding: 16px 24px; } }
</style>
</head>
<body>
  <h1>${escapeHtml(esc.id)} — ${escapeHtml(esc.titulo)}</h1>
  <div class="area">${escapeHtml(esc.area)}</div>

  <h2>Objetivo</h2>
  <p>${escapeHtml(esc.objetivo)}</p>

  <h2>Configuración inicial</h2>
  <ul>${esc.configuracionInicial.map((c) => `<li><span class="checkbox"></span>${escapeHtml(c)}</li>`).join('')}</ul>

  ${esc.cambiosConfiguracion?.length ? `
  <h2>Cambios de configuración durante la prueba</h2>
  <ul>${esc.cambiosConfiguracion.map((c) => `<li><span class="checkbox"></span>${escapeHtml(c)}</li>`).join('')}</ul>
  ` : ''}

  <h2>Paso a paso</h2>
  <ol>${esc.pasos.map((p) => `<li><span class="paso-actor">${escapeHtml(p.actor)}</span> — ${escapeHtml(p.accion)}</li>`).join('')}</ol>

  <h2>Checkpoints (qué debe cumplirse)</h2>
  <ul>${esc.checkpoints.map((c) => `<li><span class="checkbox"></span>${escapeHtml(c)}</li>`).join('')}</ul>

  ${esc.notas ? `<h2>Notas</h2><div class="notas">${escapeHtml(esc.notas)}</div>` : ''}

  <h2>Resultado</h2>
  <div class="resultado">
    <div class="resultado-campo"><b>Estado:</b> ${
      resultado.estado === 'pass'
        ? '<span class="badge badge-pass">✅ PASS</span>'
        : resultado.estado === 'fail'
          ? '<span class="badge badge-fail">❌ FAIL</span>'
          : '<span class="badge badge-none">Sin correr</span>'
    }</div>
    <div class="resultado-campo"><b>Actualizado:</b> ${fechaLegible(resultado.actualizadoEn)}${resultado.actualizadoPorNombre ? ` por ${escapeHtml(resultado.actualizadoPorNombre)}` : ''}</div>
    <div class="resultado-campo"><b>Causa / observaciones:</b></div>
    <div class="causa-box">${escapeHtml(resultado.causa) || '&nbsp;'}</div>
  </div>

  <script>
    window.onload = function () {
      setTimeout(function () { window.print(); }, 200);
    };
  </script>
</body>
</html>`;

  win.document.open();
  win.document.write(html);
  win.document.close();
}
